import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const promptAnythingProProduct: Product = {
  id: "prompt-anything-pro",
  slug: "prompt-anything-pro",
  name: "Prompt Anything Pro — 26 AI Models on Any Webpage (BYOK)",
  alternateName: "Prompt Anything Pro",
  shortName: "Prompt Anything",
  url: "https://plugmonkey.xyz/product/prompt-anything-pro/",
  chromeStoreRedirect: "/redirect/prompt-anything-pro-chrome-store/",
  description:
    "Highlight any text on any webpage and prompt 26 AI models (GPT-6, Claude Opus, Gemini, Grok) with your own API keys (BYOK). 430+ templates, auto-detection for code/email/data, prompt library, token tracking. Free Chrome extension.",
  shortDescription: "Select text → prompt 26 AI models with your own keys. 430+ smart templates.",
  metaDescription:
    "Highlight text on any webpage and prompt 26 AI models (GPT-6, Claude Opus, Gemini, Grok) with your own API keys. 430+ templates. Free Chrome extension.",
  image: ["https://plugmonkey.xyz/product/prompt-anything-pro/og-image.png"],
  icon: {
    name: "HiSparkles",
    backgroundClasses: "bg-gradient-to-br from-purple-800 via-purple-900 to-purple-950", // Dark wizard's mystical gradient
    color: "#ffffff", // white for contrast on dark purple background
  },
  category: "Software > Browser Extensions > Productivity",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Knowledge workers, creators, engineers",
  freeOffer: {
    name: "Free Version",
    description: "5 prompts per week, 50 saved library entries, all providers with BYOK",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/prompt-anything-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/3d1899b3-bbdd-4c9d-bcdb-800f17f092f4",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/ec56cec4-f89d-495b-9f38-bb019997a94d",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/6975d4d6-ecd9-4969-a1c5-044abb39ecee",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.9",
    reviewCount: "95",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "15,630",
  keywords:
    "AI prompts, BYOK, OpenAI, Anthropic, Google Gemini, prompt library, browser extension, productivity, summarization, writing, coding assistance, translation, smart text detection, template palette, token tracking, 430 templates, GPT-6, Claude Opus, Gemini 3.8, command palette, cost tracking",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.9.9",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.9.9",
  hero: {
    headline:
      "<span class='bg-yellow-200 px-1.5 py-0.5 rounded-sm' style='background-color: #fef08a;'>Highlight</span> Anything, Prompt Anything — <span class='text-primary'>Prompt Anything Pro</span> for Chrome",
    subheadline:
      "26 AI models across 7 providers. 430+ smart templates. Auto-detects code, email, data, and foreign text. Your keys, your browser, your library — no middleman.",
    ctaText: "Install Free",
    badgeText: "v1.9.9 • Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "430+ Smart Templates",
      description:
        "Auto-detects code, email, data, foreign text, URLs, and prose — then suggests relevant templates from 15 categories via a searchable command palette.",
      icon: "HiLightningBolt",
    },
    {
      title: "30+ Models, Your Keys",
      description:
        "GPT-6.1 Sol, Claude Opus 5.5, Gemini 3.8 Flash, Grok 4.7, and more across 7 providers — including local models and Chrome's built-in AI. BYOK means pennies per query with built-in token and cost tracking per provider.",
      icon: "HiShieldCheck",
    },
    {
      title: "Searchable Prompt Library",
      description:
        "Save, tag, search, and re-run any response. Bulk tag, export, and delete. Full JSON and CSV import/export for your entire history.",
      icon: "HiTemplate",
    },
  ],
  longFormFeatures: [
    {
      title: "Smart Text Detection",
      description:
        "Prompt Anything Pro auto-detects 6 content types — code, email, tabular data, foreign text, URLs, and prose — and instantly suggests the most relevant templates. Select a code block and get review, explain, test, and debug templates. Select an email and get reply, summarize, and extract action items.",
    },
    {
      title: "Command Palette for Prompts",
      description:
        "A searchable palette with keyboard navigation (/ to open, arrow keys to browse, Enter to select). Browse 430+ templates across 15 categories, pin favorites, and access recents. Type a few characters to filter instantly — like Spotlight for your prompts.",
    },
    {
      title: "Full Response Library",
      description:
        "Every response is saved with its prompt, model, token count, and timestamp. Switch between grid and list views, search by content or tag, filter by model or date, bulk-tag or bulk-delete, and re-run any saved prompt with one click. Export/import your entire library as JSON or CSV.",
    },
    {
      title: "Privacy-First, Cost-Transparent",
      description:
        "Your API keys are stored locally and masked in the UI. Requests go directly to the provider — no proxy, no middleman. Per-provider token and cost tracking shows exactly what each query costs so you stay in control of your spend.",
    },
  ],
  faq: [
    {
      question: "Do I have to use my own API keys?",
      answer:
        "Yes — Prompt Anything Pro is BYOK (Bring Your Own Keys). Connect your OpenAI, Anthropic, Google, xAI, or Groq keys directly, or run local models via Ollama/LM Studio and Chrome's built-in AI with no key at all. Keys are stored locally in your browser and masked in the UI. Requests go straight to the provider — we never see your keys or your data.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version gives you 5 prompts per week and 50 saved library entries with access to all 430+ templates and all 26 AI models. The Pro version offers unlimited prompts, unlimited library entries, custom template creation, bulk operations (tag, export, delete), re-run saved prompts, history export/import (CSV/JSON), and priority support.",
    },
    {
      question: "Does it work on any website?",
      answer:
        "Yes. If you can highlight text, you can prompt it. The floating button appears on text selection anywhere. Smart detection automatically identifies whether your selection is code, email, data, foreign text, a URL, or prose and suggests relevant templates.",
    },
    {
      question: "Is my data private?",
      answer:
        "All prompt data, keys, and history are stored locally in your browser. We don't proxy your traffic — your requests go directly to the AI provider you choose with your own keys. API keys are masked in the UI for extra security.",
    },
    {
      question: "Can I export my prompt history?",
      answer:
        "Yes. Export/import your full library as CSV or JSON. The library supports tagging, search, filtering by model or date, and re-running any saved prompt with one click.",
    },
    {
      question: "How does Prompt Anything Pro compare to Monica AI?",
      answer:
        'Prompt Anything Pro supports 26 AI models across seven providers — OpenAI, Anthropic, Google, xAI, and Groq, plus local models (Ollama/LM Studio) and Chrome\'s built-in AI — with your own API keys, giving you full model choice and data control. Monica AI is tied to a single provider and routes your data through their servers. If you value privacy, multi-model flexibility, and 430+ smart templates, Prompt Anything Pro is the stronger option. Read the <a href="/comparison/prompt-anything-pro-vs-monica-ai/" style="color: var(--primary); text-decoration: underline;">full comparison with Monica AI</a> or see how it ranks among the <a href="/best/best-ai-chrome-extensions/" style="color: var(--primary); text-decoration: underline;">best AI Chrome extensions</a>.',
    },
    {
      question: "How much does it cost with my own API keys?",
      answer:
        "Most queries cost fractions of a cent. A typical prompt with a paragraph of selected text runs about $0.002–$0.01. Heavy users report spending $2–5/month on API costs for hundreds of queries — far less than a $20/month ChatGPT Plus or Claude Pro subscription. The built-in token and cost tracker shows your exact spend per provider.",
    },
    {
      question: "What AI models are supported?",
      answer:
        "26 models across 7 providers. OpenAI: GPT-6.1 Sol, GPT-6 Astra, GPT-6.1 Terra, GPT-6 Luna, GPT-5.5. Anthropic: Claude Opus 5.5, Claude Sonnet 5.5, Claude Haiku 5.5, Claude Fable 5.1. Google: Gemini 3.8 Flash, Gemini 4 Argon, Gemini 3.6 Flash, Gemini 3.5 Flash-Lite. xAI: Grok 4.7, Grok 4.3, Grok 4.20, Grok Build 0.1. Groq: GPT-OSS 120B, GPT-OSS 20B, Qwen 3.8 27B (preview). Plus local models through Ollama or LM Studio (Llama 3.2 3B, Llama 3.1 8B, Qwen 2.5 7B, Mistral 7B, Phi-3 Mini) and Chrome's built-in AI (Gemini Nano) — both key-free and fully on-device.",
    },
    {
      question: "What are the 430+ templates?",
      answer:
        "Templates span 15 categories: Summarize, Explain, Rewrite, Translate (25 languages), Code (review, debug, test, refactor), Email (reply, extract actions), Analyze (sentiment, key points), Creative Writing, Marketing Copy, Academic, Data Extraction, Social Media, SEO, Legal, and Custom. Smart detection auto-surfaces the right category based on your selected text.",
    },
  ],
  useCases: [
    {
      title: "Writers & Editors",
      description:
        "60+ writing templates — rewrite for tone, summarize, expand, simplify. Smart prose detection auto-suggests relevant templates the moment you select text.",
    },
    {
      title: "Engineers",
      description:
        "45 coding templates + automatic code detection. Explain, review, test, debug, and refactor code blocks without leaving the page. Switch between GPT-6.1 Sol and Claude Opus 5.5 per task.",
    },
    {
      title: "Researchers",
      description:
        "Extract key points, action items, and citations from papers. 25-language translation, data extraction templates, and a searchable library to organize findings across sessions.",
    },
    {
      title: "Marketers",
      description:
        "40+ marketing and social media templates. Generate ad copy variations, social posts, and SEO meta descriptions. Bulk-export your best outputs for team sharing.",
    },
    {
      title: "Students & Academics",
      description:
        "Summarize papers, generate study notes, and get explanations at any level. Academic templates for literature reviews, thesis outlines, and citation formatting.",
    },
    {
      title: "Customer Teams",
      description:
        "Smart email detection auto-suggests reply, summarize, and extract-action-items templates. Save reusable response templates and re-run them across tickets.",
    },
  ],
  productHunt: {
    slug: "prompt-anything-pro-by-plugmonkey",
    id: "prompt-anything-pro",
    title: "Prompt Anything Pro",
    rating: "4.9",
    reviewCount: "95",
  },
  demoSection: {
    title: "See Prompt Anything Pro in Action",
    description: "Watch how easy it is to prompt any AI from anywhere.",
    slides: getProductScreenshots("prompt-anything-pro"),
  },
  relatedResources: [
    {
      category: "best",
      slug: "best-ai-chrome-extensions",
      title: "Best AI Chrome Extensions 2026",
      description: "Prompt Anything Pro ranks #1 — BYOK + multi-model inline workflow.",
    },
    {
      category: "guide",
      slug: "set-up-byok-ai-chrome",
      title: "How to Set Up BYOK AI in Chrome",
      description: "Step-by-step: OpenAI, Anthropic, Google API keys — 5 minutes per provider.",
    },
    {
      category: "faq",
      slug: "how-to-write-effective-chatgpt-prompts",
      title: "How to Write Effective ChatGPT Prompts",
      description: "7 patterns that consistently produce sharp output — with examples.",
    },
    {
      category: "faq",
      slug: "is-chatgpt-plus-worth-it",
      title: "Is ChatGPT Plus Worth It?",
      description: "Cost-benefit breakdown with the real BYOK math at every usage volume.",
    },
    {
      category: "faq",
      slug: "what-is-the-best-ai-for-coding",
      title: "What's the Best AI for Coding?",
      description: "Claude vs GPT-6.1 vs Gemini vs DeepSeek — tested across actual dev work.",
    },
    {
      category: "comparison",
      slug: "chatgpt-vs-claude",
      title: "ChatGPT vs Claude — Production Test Data",
      description: "Real first-party observations from running both via Prompt Anything Pro.",
    },
  ],
  testimonials: [
    {
      text: "I switch between Claude Opus and GPT-5 depending on the task, and this lets me do that from one shortcut without opening three tabs. Highlight, prompt, done. The command palette with keyboard nav is a game-changer — I barely touch my mouse.",
      author: "Dmitri V.",
      role: "Full-stack dev, uses AI for code review",
      rating: 5,
    },
    {
      text: "The template system is where this shines. I have 8 saved prompts for different writing tasks — summarize, rewrite formal, translate to Spanish, etc. Smart detection auto-picks the right category which saves clicks. Wish the response window was resizable, but the workflow itself is fast.",
      author: "Sana A.",
      role: "Content lead at a fintech startup",
      rating: 4,
    },
    {
      text: "BYOK means I'm paying pennies per query on my own API keys instead of $20/mo for each AI subscription. Used it ~400 times last month across Anthropic and OpenAI. The token tracker confirmed I spent under $3 total.",
      author: "Corey M.",
      role: "Solopreneur, runs 2 niche newsletters",
      rating: 5,
    },
    {
      text: "I read dense papers all day. I highlight a paragraph and run my 'explain simply' template without leaving the PDF. On my own OpenAI key it costs me less than a coffee a week.",
      author: "Wei L.",
      role: "Neuroscience PhD student",
      rating: 5,
    },
    {
      text: "I answer tickets in three languages. Highlight the message, run my translate template, draft the reply. The response window does not resize, which bugs me on long threads, but it still saves me a tab.",
      author: "Sofía R.",
      role: "Customer support, SaaS",
      rating: 4,
    },
  ],
  ctaSection: {
    title: "Highlight. Prompt. Ship. On your own keys.",
    subtitle:
      "Keep control of your data and costs while getting instant AI assistance anywhere you read online.",
  },
  howTo: {
    name: "How to Use Prompt Anything Pro",
    description:
      "Select text on any webpage, choose your provider and template, and get AI answers instantly while keeping your own keys",
    totalTime: "PT2M",
    tool: "Chrome Browser",
    steps: [
      {
        name: "Install the Extension",
        text: "Add Prompt Anything Pro to Chrome with one click.",
        url: "https://plugmonkey.xyz/redirect/prompt-anything-pro-chrome-store/",
        position: 1,
      },
      {
        name: "Select Text and Click the Floating Button",
        text: "Highlight any text on any webpage and click the floating PlugMonkey button that appears.",
        position: 2,
      },
      {
        name: "Choose Provider, Template, and Send",
        text: "Pick a template or write your own prompt, choose any of the 7 providers with your keys (or a key-free local model), then send and save the response to your library.",
        position: 3,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "HiSparkles",
    backgroundIconOpacity: "opacity-10",
    backgroundIconTransform: "transform rotate-6",
    features: [
      { label: "Flow", value: "Highlight → Prompt" },
      { label: "Privacy", value: "BYOK" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 4,
  headerOrder: 800, // Second - shown second in header
  pricing: {
    tagline: "Select any text and prompt 26 AI models instantly using your own API keys.",
    freeFeatures: [
      "5 prompts per week (resets every Monday)",
      "50 saved prompt-response entries",
      "All 430+ templates across 15 categories",
      "All 26 AI models (GPT-6.1 Sol, Claude Opus 5.5, Gemini 3.8 Flash, Grok 4.7, and more)",
      "Smart text detection (code, email, data, foreign text, URLs, prose)",
      "BYOK for OpenAI, Anthropic, Google, xAI, Groq — plus key-free local models; keys stored locally",
    ],
    proFeatures: [
      "Unlimited prompts and library entries",
      "Custom template creation",
      "Bulk operations (tag, export, delete)",
      "Re-run any saved prompt with one click",
      "Switch providers/models on the fly (7 providers, cloud to on-device)",
      "Save, tag, search, and export/import library (CSV/JSON)",
      "Usage tracking (tokens/cost per provider) and prompt history",
      "Priority customer support",
      "All future updates included",
    ],
    useCase:
      "Perfect for researchers, writers, engineers, and analysts who want fast, private prompting across 26 models with their own keys.",
  },
  flowData: {
    installation: {
      headline: "Get answers without leaving the page",
      subheadline: "Select text, click the floating button, and prompt AI instantly.",
      guideSteps: [
        {
          title: "Install & pin the extension",
          description: "Add Prompt Anything Pro to Chrome and keep it handy on your toolbar.",
          image: "pin-extension",
        },
        {
          title: "Select Text on Any Page",
          description:
            "Highlight any text on any webpage. Smart detection identifies code, email, data, and more. A floating PlugMonkey button appears near your selection — click it.",
          image: "select-text",
          screenshot: "screenshot-1.png",
          ctaText: "Try Selecting Text",
          ctaUrl: "https://en.wikipedia.org/wiki/Artificial_intelligence",
        },
        {
          title: "Pick a Template & Prompt AI",
          description:
            "Choose from 430+ smart templates across 15 categories, pick your AI provider and model, and get instant results.",
          image: "choose-template",
          screenshot: "screenshot-2.png",
        },
      ],
      primaryCtaText: "Select Some Text and Try It",
      primaryCtaUrl: "https://en.wikipedia.org/wiki/Artificial_intelligence",
      emailCapture: {
        headline: "Get power prompts & tips",
        description: "Join the list for advanced prompt templates and new model updates.",
        buttonText: "Send me the prompts",
      },
    },
    uninstallation: {
      headline: "Leaving so soon?",
      subheadline: "Tell us why and we'll improve the experience.",
      survey: {
        question: "What made you uninstall?",
        options: [
          "Wanted more free prompts",
          "Templates/models felt limited",
          "Issues with my API keys",
          "UI/UX needs improvement",
          "Found another tool",
        ],
      },
    },
  },
};
